import React, { useEffect } from 'react';
import ModuleTabs from './ModuleTabs';
import LayerOverlay from './LayerOverlay';
import './EntityInspector.css';

/* Inspector contextual de registro (F2, ESTANDAR §8 + brief §10).
   UNA sola variante (decisión F4.4 de Miguel, principio rector §0): drawer
   deslizante desde la derecha vía Portal, ancho token --inspector-w — el
   patrón de Pedidos ES el patrón. La variante 'docked' (empotrada en el grid
   del módulo) murió: dos comportamientos para la misma acción eran
   inconsistencia pura, y comprimía la tabla de al lado.
   En móvil (<768px) ocupa la pantalla completa (CSS).
   Estructura: resumen arriba → contenido con scroll propio → acciones FIJAS
   al pie (§8). Sin subtabs en los módulos (regla del molde); la capacidad
   `tabs` queda solo para casos que la amparen explícitamente. */
export default function EntityInspector({
  open = false,
  title,
  subtitle = '',
  status = null,            // nodo (StatusBadge / chips)
  media = null,             // nodo (imagen/avatar del registro)
  tabs = null,              // { tabs, active, onChange }
  actions = null,           // nodo — SIEMPRE visible al pie (lugares FIJOS: las
                            // acciones no juegan a las sillas — principio F3.2)
  headerAction = null,      // acción CONTEXTUAL junto al título (se AGREGA,
                            // nunca reemplaza a una del pie)
  onClose,
  onPrev = null,
  onNext = null,
  onOpenFullPage = null,
  children,
  className = '',
  overlayClassName = '',    // clase EXTRA para la capa (p.ej. subir el z-index cuando
                            // el drawer se abre encima de otra capa, como el POS)
}) {
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowUp' && onPrev) { e.preventDefault(); onPrev(); }
      if (e.key === 'ArrowDown' && onNext) { e.preventDefault(); onNext(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose, onPrev, onNext]);

  if (!open) return null;

  return (
    <LayerOverlay open className={`ui-inspector-overlay${overlayClassName ? ` ${overlayClassName}` : ''}`} onBackdrop={onClose}>
      <section className={`ui-inspector ui-inspector--drawer${className ? ` ${className}` : ''}`} aria-label={typeof title === 'string' ? title : 'Detalle'}>
        <header className="ui-inspector-header">
          {media && <div className="ui-inspector-media">{media}</div>}
          <div className="ui-inspector-headcopy">
            <h3 className="ui-inspector-title">{title}</h3>
            {subtitle && <p className="ui-inspector-subtitle">{subtitle}</p>}
            {status && <div className="ui-inspector-status">{status}</div>}
            {headerAction && <div className="ui-inspector-headaction">{headerAction}</div>}
          </div>
          <div className="ui-inspector-nav">
            {(onPrev || onNext) && (
              <>
                <button type="button" className="ui-inspector-navbtn" onClick={onPrev} disabled={!onPrev} aria-label="Registro anterior">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15" /></svg>
                </button>
                <button type="button" className="ui-inspector-navbtn" onClick={onNext} disabled={!onNext} aria-label="Registro siguiente">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                </button>
              </>
            )}
            {onOpenFullPage && (
              <button type="button" className="ui-inspector-navbtn" onClick={onOpenFullPage} aria-label="Abrir en página completa" title="Abrir en página completa">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6" /><path d="M10 14L21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></svg>
              </button>
            )}
            <button type="button" className="ui-inspector-navbtn" onClick={onClose} aria-label="Cerrar">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
          </div>
        </header>

        {tabs && <div className="ui-inspector-tabs"><ModuleTabs {...tabs} variant="underline" /></div>}

        <div className="ui-inspector-body bottom-sheet-content">{children}</div>

        {actions && <footer className="ui-inspector-actions">{actions}</footer>}
      </section>
    </LayerOverlay>
  );
}
